/**
 * Edit Note dialog — mirrors Angular `NoteEditComponent` + dialog title "Edit Note".
 * POST `/api/notes_edit/:notes_id` with `{ notes }`.
 */
import { useState, useEffect } from "react"
import { Loader2 } from "lucide-react"
import apiClient from "@/lib/api/apiClient"
import { showError, formatApiError } from "@/lib/utils/notifications"
import {
  MentionTextarea,
  applyTaggingToNotes,
} from "@/components/notes/MentionTextarea"
import { OnScreenModal } from "@/components/ui/OnScreenModal"
import modalStyles from "@/features/application/styles/modals.module.css"

export interface EditNoteModalProps {
  isOpen: boolean
  notesId: string | number | null
  initialNotes: string
  onClose: () => void
  onSaved: () => void
  /**
   * Prefix for `data-testid` on overlay, panel, close, form, textarea, submit.
   * Example: `company-internal-notes-edit` → `company-internal-notes-edit-overlay`, etc.
   */
  testIdPrefix?: string
}

function extractNotesFieldError(err: unknown): string | null {
  const o = err as {
    data?: { errors?: { notes?: string[] } }
    response?: { data?: { errors?: { notes?: string[] } } }
  }
  const body = o?.data ?? o?.response?.data
  const notes = body?.errors?.notes
  if (Array.isArray(notes) && notes[0] != null && String(notes[0]).trim()) {
    return String(notes[0])
  }
  return null
}

export function EditNoteModal({ isOpen, notesId, initialNotes, onClose, onSaved, testIdPrefix }: EditNoteModalProps) {
  const tid = (suffix: string) => (testIdPrefix ? `${testIdPrefix}-${suffix}` : undefined)
  const [text, setText] = useState("")
  const [taggingContent, setTaggingContent] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setText(initialNotes ?? "")
      setTaggingContent({})
    }
  }, [isOpen, initialNotes])

  if (!isOpen || notesId == null || String(notesId) === "") return null

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()
    const trimmed = text.trim()
    if (!trimmed) {
      showError("Note is required.")
      return
    }
    setSubmitting(true)
    try {
      const noteBody = applyTaggingToNotes(trimmed, taggingContent)
      await apiClient.post(`/api/notes_edit/${notesId}`, { notes: noteBody })
      onSaved()
      onClose()
    } catch (err: unknown) {
      const line = extractNotesFieldError(err)
      showError(line || formatApiError(err as Record<string, unknown>))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <OnScreenModal
      isOpen={isOpen}
      title="Edit Note"
      onClose={() => !submitting && onClose()}
      maxWidth="max-w-lg"
      bodyScrollOnly
      headerVariant="centeredTitle"
      /* Above UserManagementEditEmbeddedModal (z-10002) when edit page is in a dialog */
      overlayClassName="!z-[10041]"
      overlayDataTestId={tid("overlay")}
      panelDataTestId={tid("panel")}
      closeButtonDataTestId={tid("close-button")}
      titleDataTestId={tid("title")}
      footer={
        <div className={modalStyles.modalFooterActionGroup}>
          <button
            data-testid={tid("submit-button")}
            type="submit"
            form="edit-note-form"
            disabled={submitting || !text.trim()}
            className={modalStyles.modalActionPrimary}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit"}
          </button>
        </div>
      }
    >
      <form id="edit-note-form" data-testid={tid("form")} onSubmit={handleSubmit} className="space-y-2">
        <label htmlFor="edit-note-textarea" className="mb-2 block text-sm font-medium text-slate-600">
          Edit Note
        </label>
        <MentionTextarea
          textareaId="edit-note-textarea"
          dataTestId={tid("textarea")}
          rows={5}
          value={text}
          onChange={setText}
          disabled={submitting}
          placeholder="Edit note… (use @ to mention Liberty users)"
          className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:border-[rgb(21_151_255)] focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)]/30"
          onTaggingAdd={(displayKey, useridToken) => {
            setTaggingContent((prev) => ({
              ...prev,
              [displayKey]: useridToken,
            }))
          }}
        />
      </form>
    </OnScreenModal>
  )
}
